import React, { useEffect, useRef } from 'react';
import { initializeAnswerEditors } from '@omq/shared/src/hooks/answers/code-editor';
import { SandboxedHtml } from '@omq/shared';
import type { IAnswerCodeElement } from '@omq/answer-code-element';
import { NavigateButton } from './NavigateButton';
import { PaperclipIcon } from './icons';

// host theme variables forwarded into the snippet iframe so it matches the chatbot look
const SNIPPET_THEME_VARS = [
  '--omq-primary-color',
  '--omq-background-color',
  '--omq-text-color',
  '--omq-text-muted-color',
  '--omq-border-color',
  '--omq-border-radius',
  '--omq-font-family',
] as const;

export interface Message {
  id: string;
  type: 'sent' | 'received' | 'system';
  content: string;
  timestamp: Date;
  isHtml?: boolean;
  variant?: 'html-snippet';
  navigateUrl?: string;
  // Marks failure notices (connection/API/upload errors) so they render with
  // role="alert"; informational system messages stay non-alert.
  isError?: boolean;
  attachment?: {
    path: string;
    mimeType: string;
    name: string;
  };
  attachments?: Array<{
    path: string;
    mimeType: string;
    name: string;
  }>;
}

interface MessageListProps {
  messages: Message[];
  onNavigate?: (url: string) => void;
  onImageClick?: (image: { src: string; alt: string }) => void;
  ariaLabel?: string;
  // Visually-hidden authorship prefix for sent messages ("You")
  youLabel?: string;
  // Visually-hidden authorship prefix for received messages (bot name)
  botLabel?: string;
  // Accessible name for html-snippet iframes
  snippetTitle?: string;
  // BCP47 language of the chat content, forwarded to snippet iframes
  lang?: string;
  // Localized "Continue to {0}" template for navigate buttons
  continueTemplate?: string;
  // Localized "opens in new tab" hint for external navigate links
  opensNewTabLabel?: string;
}

/**
 * MessageList component
 * Displays the list of chat messages
 */
export const MessageList: React.FC<MessageListProps> = ({
  messages,
  onNavigate,
  onImageClick,
  ariaLabel,
  youLabel,
  botLabel,
  snippetTitle,
  lang,
  continueTemplate,
  opensNewTabLabel,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const editorsRef = useRef<IAnswerCodeElement[]>([]);

  // Initialize CodeMirror on source code elements in HTML messages
  useEffect(() => {
    if (!listRef.current) {
      return;
    }

    let isMounted = true;

    initializeAnswerEditors(listRef.current).then((newEditors) => {
      if (!isMounted) {
        newEditors.forEach((editor) => editor.destroy());
        return;
      }
      editorsRef.current = [...editorsRef.current, ...newEditors];
    });

    return () => {
      isMounted = false;
    };
  }, [messages]);

  // Cleanup all editors on unmount
  useEffect(() => {
    return () => {
      editorsRef.current.forEach((editor) => editor.destroy());
    };
  }, []);

  // Delegate click and keyboard activation on images inside HTML content to
  // onImageClick, and make those images focusable (they open the lightbox).
  useEffect(() => {
    if (!listRef.current || !onImageClick) {
      return;
    }

    const container = listRef.current;

    const activate = (target: HTMLElement): boolean => {
      if (target.tagName === 'IMG' && target.closest('.omq-chatbot-message-content')) {
        const img = target as HTMLImageElement;
        onImageClick({ src: img.src, alt: img.alt || '' });
        return true;
      }
      return false;
    };

    const handleClick = (e: MouseEvent) => {
      activate(e.target as HTMLElement);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' && e.key !== ' ') {
        return;
      }
      if (activate(e.target as HTMLElement)) {
        e.preventDefault();
      }
    };

    // Lightbox-openable content images need to be reachable by keyboard. Only
    // images with a real text alternative are promoted: decorative/alt-less
    // images (spacers, inline icons) would become unnamed 'button' tab stops
    // that open an empty lightbox and clutter keyboard navigation.
    container
      .querySelectorAll<HTMLImageElement>('.omq-chatbot-message-content img')
      .forEach((img) => {
        if (img.alt.trim()) {
          img.setAttribute('role', 'button');
          img.tabIndex = 0;
        }
      });

    container.addEventListener('click', handleClick);
    container.addEventListener('keydown', handleKeyDown);
    return () => {
      container.removeEventListener('click', handleClick);
      container.removeEventListener('keydown', handleKeyDown);
    };
  }, [onImageClick, messages]);

  const formatTime = (date: Date) => {
    return new Date(date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  /**
   * Check if this message is the last in a consecutive block of the same type.
   * Used for received messages only — sent messages always show their timestamp.
   */
  const isLastInBlock = (index: number): boolean => {
    const current = messages[index];
    const next = messages[index + 1];
    // Last message overall, or next message is a different type
    return !next || next.type !== current.type;
  };

  return (
    <div
      className="omq-chatbot-message-list"
      ref={listRef}
      role="log"
      aria-live="polite"
      aria-label={ariaLabel || 'Chat messages'}>
      {messages.map((message, index) => {
        if (message.type === 'system') {
          return (
            <div
              key={message.id}
              className="omq-chatbot-system-message"
              role={message.isError ? 'alert' : undefined}>
              {message.content}
            </div>
          );
        }

        if (message.variant === 'html-snippet') {
          return (
            <SandboxedHtml
              key={message.id}
              className="omq-chatbot-html-snippet"
              html={message.content}
              themeVars={SNIPPET_THEME_VARS}
              title={snippetTitle || 'Embedded content'}
              lang={lang}
            />
          );
        }

        // Sent messages always show timestamp; received messages only on last in block
        const showTimestamp = message.type === 'sent' || isLastInBlock(index);

        return (
          <div
            key={message.id}
            className={`omq-chatbot-message omq-chatbot-message-${message.type}`}>
            <div className="omq-chatbot-message-bubble">
              <span className="omq-sr-only">
                {message.type === 'sent'
                  ? `${youLabel || 'You'}: `
                  : `${botLabel || 'Chatbot'}: `}
              </span>
              {(message.attachments || (message.attachment ? [message.attachment] : [])).length >
                0 && (
                <div className="omq-chatbot-message-attachments">
                  {(message.attachments || [message.attachment!]).map((att, attIdx) => (
                    <div key={attIdx} className="omq-chatbot-attachment">
                      {att.mimeType.startsWith('image/') ? (
                        <img
                          src={att.path}
                          alt={att.name}
                          className="omq-chatbot-attachment-image"
                          style={message.type === 'received' && onImageClick ? { cursor: 'pointer' } : undefined}
                          role={message.type === 'received' && onImageClick ? 'button' : undefined}
                          tabIndex={message.type === 'received' && onImageClick ? 0 : undefined}
                          onClick={
                            message.type === 'received' && onImageClick
                              ? () => onImageClick({ src: att.path, alt: att.name })
                              : undefined
                          }
                          onKeyDown={
                            message.type === 'received' && onImageClick
                              ? (e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    onImageClick({ src: att.path, alt: att.name });
                                  }
                                }
                              : undefined
                          }
                        />
                      ) : message.type === 'received' ? (
                        <a
                          href={att.path}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="omq-chatbot-attachment-file">
                          <span className="omq-chatbot-attachment-icon" aria-hidden="true">
                            <PaperclipIcon />
                          </span>
                          <span className="omq-chatbot-attachment-name">{att.name}</span>
                        </a>
                      ) : (
                        <span className="omq-chatbot-attachment-file">
                          <span className="omq-chatbot-attachment-icon" aria-hidden="true">
                            <PaperclipIcon />
                          </span>
                          <span className="omq-chatbot-attachment-name">{att.name}</span>
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {message.isHtml ? (
                <div
                  className="omq-chatbot-message-content"
                  dangerouslySetInnerHTML={{ __html: message.content }}
                />
              ) : (
                <div className="omq-chatbot-message-content">{message.content}</div>
              )}
            </div>
            {showTimestamp && (
              <div className="omq-chatbot-message-time">{formatTime(message.timestamp)}</div>
            )}
            {message.navigateUrl && onNavigate && (
              <NavigateButton
                url={message.navigateUrl}
                onNavigate={onNavigate}
                continueTemplate={continueTemplate}
                opensNewTabLabel={opensNewTabLabel}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
